import React, { ReactElement } from "react";
import { PillarTableColumnProps } from "shared/components/pillar-table/columns/PillarTableColumn";

interface GetFixedColumnStyleParams<T> {
  column: ReactElement<PillarTableColumnProps<T>>;
  index: number;
  fixedColumnWidths: number[];
  customStyles?: string | null;
  isHeaderCell?: boolean;
}

export enum PillarDataTableTestModes {
  exists = "exists",
  notExists = "notExists",
}

export type PillarDataTableColumn = {
  position?: number; // Note how position is optional
  testid: string;
  value: string;
  mode: PillarDataTableTestModes;
};

export type PillarDataTableRow = {
  position?: number; // Note how position is optional
  columns:
    | PillarDataTableColumn[]
    | {
        columns: PillarDataTableColumn[];
        nestedRows: PillarDataTableRow[];
      };
  mode: PillarDataTableTestModes;
};

export type ExpectPillarDataTableProps = {
  columns?: PillarDataTableColumn[];
  rows?: PillarDataTableRow[];
  exactMatch?: boolean; //default false
  contains?: string;
  name?: string;
};

export enum PillarDataTableFixedColumn {
  left = "left",
  right = "right",
}

export const getFixedColumnStyle = <T>({
  column,
  index,
  fixedColumnWidths,
  customStyles,
  isHeaderCell,
}: GetFixedColumnStyleParams<T>): React.CSSProperties => {
  const commonStyles: React.CSSProperties = {
    position: "sticky",
    zIndex: 10,
    backgroundColor:
      customStyles ??
      "hsl(var(--color-table-header-background-hue) var(--color-table-header-background-saturation) var(--color-table-header-background-lightness) / var(--tw-bg-opacity, 1))",
  };

  let leftOffset = 0;
  let rightOffset = 0;

  const sumWidths = (widths: number[]): number =>
    widths.reduce((acc, width) => (width ? acc + Math.ceil(width) : acc), 0);

  if (column.props?.fixedColumn === PillarDataTableFixedColumn.left) {
    leftOffset = sumWidths(fixedColumnWidths.slice(0, index));

    return {
      ...commonStyles,
      left: `${leftOffset}px`,
      borderRight: "2px solid",
    };
  }

  if (column.props?.fixedColumn === PillarDataTableFixedColumn.right) {
    const sliceStart = isHeaderCell ? index + 1 : index + 2;

    rightOffset = sumWidths(fixedColumnWidths.slice(sliceStart));

    return {
      ...commonStyles,
      right: `${rightOffset}px`,
      borderLeft: "2px solid",
    };
  }

  return {
    zIndex: 7,
  };
};
